import React, { useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, ZoomControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { FiMapPin, FiPhone, FiUser, FiUsers, FiX } from 'react-icons/fi';
import '../styles/components/PhilippinesMap.css';

// Fix for default marker icon in Leaflet with React
delete L.Icon.Default.prototype._getIconUrl;

// Custom marker icons
const createCustomIcon = (color) => {
  return new L.DivIcon({
    className: 'custom-marker',
    html: `
      <div class="marker-pin ${color}">
        <svg viewBox="0 0 24 24" fill="currentColor" width="32" height="32">
          <path d="M12 0C7.58 0 4 3.58 4 8c0 5.25 8 13 8 13s8-7.75 8-13c0-4.42-3.58-8-8-8zm0 11c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z"/>
        </svg>
      </div>
    `,
    iconSize: [32, 42],
    iconAnchor: [16, 42],
    popupAnchor: [0, -42]
  });
};

const redIcon = createCustomIcon('red');
const greenIcon = createCustomIcon('green');

const PhilippinesMap = ({ areasOfOperation }) => {
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showInfoPanel, setShowInfoPanel] = useState(false);

  // Philippines center coordinates
  const philippinesCenter = [12.8797, 121.7740];

  // All locations with coordinates - pulling complete info from areasOfOperation
  const locations = {
    headOffice: {
      name: 'HEAD OFFICE - DAVAO CITY',
      type: 'headquarters',
      coords: [7.0686, 125.6117],
      address: areasOfOperation.headOffice.address,
      phone: areasOfOperation.headOffice.phone,
      manager: { name: 'Avelino A. Hermoso', position: 'General Manager' },
      staff: [
        { name: 'Myra Betonio', position: 'Senior Operation Manager', contact: '0998-593-0905' },
        { name: 'Dave Cueme', position: 'Branch Operation Manager (Region 11, 12, 8)', contact: '0919-067-9028' },
        { name: 'Rudyrick John Perez', position: 'Branch Operation Manager (Region 6, 7, 9)', contact: '0919-077-8394' }
      ],
      coordinators: [
        { name: 'Nickole Christian Endraca', position: 'South Cluster Head', contact: '0999-229-5941' },
        { name: 'Noel Futalan', position: 'North Cluster Head', contact: '0998-593-0904' },
        { name: 'Jonnifer Vinson', position: 'Cluster Head', contact: '0916-910-3029' }
      ]
    },
    branches: [
      {
        name: 'LUZON BRANCH',
        type: 'branch',
        coords: [14.5790, 121.0359],
        address: '68 EDSA cor Pinatubo St. Barangay Malamig, Mandaluyong City',
        manager: { name: 'Frosie Adecer', position: 'Luzon General Manager' },
        staff: [
          { name: 'Arlyn Abellera', position: 'Luzon Office Admin' },
          { name: 'Charmane Fernandez', position: 'Luzon Office Admin-Billing Staff', contact: '0920-940-0781' },
          { name: 'Mark Jayson Retardo', position: 'Luzon Office Admin-Billing Staff', contact: '0920-940-0781' }
        ],
        coordinators: [
          { name: 'Francisco, Walter', position: 'Supervisor', contact: '0927-294-3707' },
          { name: 'Raizen Rivera', position: 'NCR Coordinator', contact: '0998-845-1139' },
          { name: 'Jeffrey Delumen', position: 'NCR Coordinator', contact: '0918-929-2673' },
          { name: 'Gershon Fin', position: 'NCR Coordinator', contact: '0920-907-9561' },
          { name: 'Brian Carl Y Asahan', position: 'NCR Coordinator', contact: '0918-919-7531' },
          { name: 'Paul Andrew Mallari', position: 'NCR Coordinator', contact: '0976-025-7472' },
          { name: 'Julius Unson', position: 'North Luzon Coordinator', contact: '0920-978-1611' },
          { name: 'Christopher Bañas', position: 'South Luzon Coordinator', contact: '0999-226-2397' },
          { name: 'Maryjoy D. Dela Cruz', position: 'South Luzon Coordinator', contact: '0998-553-7253' },
          { name: 'Rodel Patente', position: 'South Luzon Coordinator', contact: '0919-076-8026' }
        ],
        areas: ['NCR', 'Region 1', 'Region 2', 'Region 3', 'Region 4A', 'Region 4B', 'Region 5', 'CAR']
      },
      {
        name: 'VISAYAS - BACOLOD',
        type: 'branch',
        coords: [10.6407, 122.9669],
        address: 'Mackinly Baybay St. cor. Sto. Niño Brgy. Mambulac, Silay City, Negros Occidental',
        manager: { name: 'Rudyrick John Perez', position: 'Branch Operation Manager' },
        phone: '0919-077-8394',
        coordinators: [
          { name: 'Marlon A. Sueta', position: 'Coordinator', contact: '0927-294-3707' },
          { name: 'Joseph Malayan', position: 'Regional Area Head', contact: '0949-993-0301' }
        ],
        areas: ['Bacolod', 'Negros Occidental']
      },
      {
        name: 'VISAYAS - CEBU',
        type: 'branch',
        coords: [10.3157, 123.8854],
        address: '6th St, Sitio Kana-as, Guadalajara, Guadalupe Cebu City',
        manager: { name: 'Rudyrick John Perez', position: 'Branch Operation Manager' },
        phone: '0919-077-8394',
        staff: [
          { name: 'Ge-R Gemina', position: 'Admin Staff', contact: '0926-007-1549' }
        ],
        coordinators: [
          { name: 'Kenneth Gordon', position: 'Coordinator', contact: '0936-814-9946' },
          { name: 'Marc Nicole Z. Gemina', position: 'Coordinator', contact: '0968-851-3739' },
          { name: 'Joseph Malayan', position: 'Regional Area Head', contact: '0949-993-0301' }
        ],
        areas: ['Cebu City', 'Cebu Province']
      },
      {
        name: 'VISAYAS - DUMAGUETE',
        type: 'branch',
        coords: [9.3068, 123.3054],
        address: 'South Bacacay, Dumaguete City',
        manager: { name: 'Rudyrick John Perez', position: 'Branch Operation Manager' },
        phone: '0919-077-8394',
        coordinators: [
          { name: 'Rene Arajji', position: 'Coordinator', contact: '0908-891-9353' },
          { name: 'Joseph Malayan', position: 'Regional Area Head', contact: '0949-993-0301' }
        ],
        areas: ['Dumaguete City', 'Negros Oriental']
      },
      {
        name: 'VISAYAS - ILOILO',
        type: 'branch',
        coords: [10.7202, 122.5621],
        address: '206 MH Del Pilar St. Molo, Iloilo City',
        manager: { name: 'Rudyrick John Perez', position: 'Branch Operation Manager' },
        phone: '0919-077-8394',
        coordinators: [
          { name: 'R-Jee Herrera', position: 'Coordinator', contact: '0998-891-9353' },
          { name: 'Joseph Malayan', position: 'Regional Area Head', contact: '0949-993-0301' }
        ],
        areas: ['Iloilo City', 'Iloilo Province']
      },
      {
        name: 'VISAYAS - BOHOL',
        type: 'branch',
        coords: [9.6500, 123.8516],
        address: 'Felis Garbiela Bldg. Castillo - Facifico Road Barangay Dao, Tagbilaran Bohol',
        manager: { name: 'Rudyrick John Perez', position: 'Branch Operation Manager' },
        phone: '0919-077-8394',
        coordinators: [
          { name: 'Rufino Urasaga', position: 'Coordinator', contact: '0912-833-9144' },
          { name: 'Joseph Malayan', position: 'Regional Area Head', contact: '0949-993-0301' }
        ],
        areas: ['Bohol Province', 'Tagbilaran City']
      },
      {
        name: 'VISAYAS - TACLOBAN',
        type: 'branch',
        coords: [11.2543, 124.9634],
        address: 'Palo, Alang-Alang Marasbaras, Tacloban City',
        manager: { name: 'Dave Cueme', position: 'Branch Operation Manager' },
        phone: '0919-067-9028',
        coordinators: [
          { name: 'Lanze Audrie Roice T. Daquiado', position: 'Coordinator', contact: '0946-455-6478' }
        ],
        areas: ['Leyte', 'Tacloban City']
      },
      {
        name: 'VISAYAS - MAASIN (Southern Leyte)',
        type: 'branch',
        coords: [10.1314, 124.8506],
        address: 'P. Gonzales St. Abgao, Maasin City',
        manager: { name: 'Dave Cueme', position: 'Branch Operation Manager' },
        phone: '0919-067-9028',
        coordinators: [
          { name: 'Renan Alizada', position: 'Coordinator', contact: '0999-225-1439' }
        ],
        areas: ['Southern Leyte', 'Maasin City']
      },
      {
        name: 'VISAYAS - ORMOC',
        type: 'branch',
        coords: [11.0069, 124.6075],
        address: 'District 21 San Vedal and Real St. Ormoc City',
        manager: { name: 'Dave Cueme', position: 'Branch Operation Manager' },
        phone: '0919-067-9028',
        coordinators: [
          { name: 'Dennis Albarando', position: 'Coordinator', contact: '0951-045-2701' }
        ],
        areas: ['Ormoc City', 'Leyte']
      },
      {
        name: 'VISAYAS - CATBALOGAN (Samar)',
        type: 'branch',
        coords: [11.7752, 124.8858],
        address: 'Eastwest Bank Brgy. 5 Curry Avenue, Corner San Bartolome St. Catbalogan City',
        manager: { name: 'Dave Cueme', position: 'Branch Operation Manager' },
        phone: '0919-067-9028',
        coordinators: [
          { name: 'Daniel Diocton', position: 'Coordinator', contact: '0985-854-3046' }
        ],
        areas: ['Samar', 'Catbalogan City']
      },
      {
        name: 'MINDANAO - BUTUAN',
        type: 'branch',
        coords: [8.9475, 125.5406],
        address: 'Dr. 3 Sevilla Apt. Nonan Village Barangay Bayanihan JC Aquino Avenue, Butuan City',
        manager: { name: 'Reymart Ligue', position: 'Regional Area Head' },
        phone: '0968-867-2181',
        staff: [
          { name: 'Alma Bada', position: 'Admin Staff', contact: '0998-593-0903' }
        ],
        coordinators: [
          { name: 'Allan Lobo', position: 'Coordinator', contact: '0998-590-7833' }
        ],
        areas: ['Butuan City', 'Agusan del Norte']
      },
      {
        name: 'MINDANAO - CAGAYAN DE ORO',
        type: 'branch',
        coords: [8.4542, 124.6319],
        address: 'Bontong, Camaman-an Cagayan de Oro',
        manager: { name: 'Reymart Ligue', position: 'Regional Area Head' },
        phone: '0968-867-2181',
        staff: [
          { name: 'Flordeliza Bacongius', position: 'Admin Staff', contact: '0908-864-1056' }
        ],
        coordinators: [
          { name: 'Mark Anthony M. Carreon', position: 'Coordinator', contact: '0975-824-3825' }
        ],
        areas: ['Cagayan de Oro', 'Misamis Oriental']
      },
      {
        name: 'MINDANAO - DIPOLOG',
        type: 'branch',
        coords: [8.5894, 123.3411],
        address: 'Dipolog City',
        manager: { name: 'Rudyrick John Perez', position: 'Branch Operation Manager' },
        phone: '0919-077-8394',
        coordinators: [
          { name: 'Sally Bantilan', position: 'Coordinator', contact: '0965-785-1898' }
        ],
        areas: ['Dipolog City', 'Zamboanga del Norte']
      },
      {
        name: 'MINDANAO - GENERAL SANTOS',
        type: 'branch',
        coords: [6.1164, 125.1716],
        address: 'No. 55 Manga Street, Barangay Dadiangas North, General Santos City',
        manager: { name: 'Dave Cueme', position: 'Branch Operation Manager' },
        phone: '0919-067-9028',
        staff: [
          { name: 'Lezlie Ocampo', position: 'Admin Staff', contact: '0910-656-0709' }
        ],
        coordinators: [
          { name: 'Gerald Lamata', position: 'Coordinator', contact: '0927-479-6364' }
        ],
        areas: ['General Santos City', 'South Cotabato']
      },
      {
        name: 'MINDANAO - MALAYBALAY',
        type: 'branch',
        coords: [8.1575, 125.1276],
        address: 'Malaybalay Water District/Primewater Sayre Highway, Malaybalay, Bukidnon',
        manager: { name: 'Reymart Ligue', position: 'Regional Area Head' },
        phone: '0968-867-2181',
        coordinators: [
          { name: 'Raniel Cameros', position: 'Coordinator', contact: '0998-590-7830' }
        ],
        areas: ['Malaybalay', 'Bukidnon']
      },
      {
        name: 'MINDANAO - VILLANUEVA (Misamis)',
        type: 'branch',
        coords: [8.5716, 124.7747],
        address: 'Bypass Road, Villanueva Misamis Oriental',
        manager: { name: 'Reymart Ligue', position: 'Regional Area Head' },
        phone: '0968-867-2181',
        staff: [
          { name: 'April Rose Ombra / Cleo Mausisa', position: 'Admin Staff' }
        ],
        coordinators: [
          { name: 'Nicholas Rafael Morga', position: 'Coordinator', contact: '0998-991-2495' },
          { name: 'Mila Aucila', position: 'Coordinator', contact: '0998-585-9108' }
        ],
        areas: ['Misamis Oriental', 'Villanueva']
      },
      {
        name: 'MINDANAO - OZAMIZ',
        type: 'branch',
        coords: [8.1481, 123.8411],
        address: 'Purok2, Badong Exec. Village Barangay Carangan, Ozamiz City',
        manager: { name: 'Rudyrick John Perez', position: 'Branch Operation Manager' },
        phone: '0919-077-8394',
        coordinators: [
          { name: 'Samson L. Gravino', position: 'Coordinator', contact: '0919-067-9025' }
        ],
        areas: ['Ozamiz City', 'Misamis Occidental']
      },
      {
        name: 'MINDANAO - PAGADIAN',
        type: 'branch',
        coords: [7.8268, 123.4365],
        address: 'Purok Kamansi, Lenienza Pagadian City',
        manager: { name: 'Rudyrick John Perez', position: 'Branch Operation Manager' },
        phone: '0919-077-8394',
        staff: [
          { name: 'Jan Roe N. Antimano', position: 'Admin Staff', contact: '0951-533-7200' }
        ],
        coordinators: [
          { name: 'Neil Genciane', position: 'Coordinator', contact: '0968-850-2837' },
          { name: 'Toto Genciane', position: 'Operation Manager' }
        ],
        areas: ['Pagadian City', 'Zamboanga del Sur']
      },
      {
        name: 'MINDANAO - SURIGAO',
        type: 'branch',
        coords: [9.7894, 125.4962],
        address: 'San Juan Lipata Road 7, Barangay Sabang, Surigao City',
        manager: { name: 'Dave Cueme', position: 'Branch Operation Manager' },
        phone: '0919-067-9028',
        coordinators: [
          { name: 'Christian Olalo', position: 'Coordinator', contact: '0998-586-6867' }
        ],
        areas: ['Surigao City', 'Surigao del Norte']
      },
      {
        name: 'MINDANAO - VALENCIA',
        type: 'branch',
        coords: [7.9006, 125.0938],
        address: 'Purok-13-A Pobalcion Hagkol, Valencia City Bukidnon',
        manager: { name: 'Reymart Ligue', position: 'Regional Area Head' },
        phone: '0968-867-2181',
        coordinators: [
          { name: 'Nestor Villanueva', position: 'Coordinator', contact: '0949-993-0306' }
        ],
        areas: ['Valencia City', 'Bukidnon']
      },
      {
        name: 'MINDANAO - ZAMBOANGA',
        type: 'branch',
        coords: [6.9214, 122.0790],
        address: 'Zamboanga City',
        manager: { name: 'Rudyrick John Perez', position: 'Branch Operation Manager' },
        phone: '0919-077-8394',
        coordinators: [
          { name: 'Cris Umpad', position: 'Coordinator', contact: '0951-045-2701' },
          { name: 'Gian Gellor', position: 'Coordinator' }
        ],
        areas: ['Zamboanga City', 'Zamboanga Peninsula']
      },
      {
        name: 'MINDANAO - BARMM (Cotabato)',
        type: 'branch',
        coords: [7.2047, 124.4346],
        address: 'Door 4, Enilo Apartment RH-4 Sinsuat',
        manager: { name: 'Dave Cueme', position: 'Branch Operation Manager' },
        phone: '0919-067-9028',
        coordinators: [
          { name: 'Ryan Robles', position: 'Coordinator', contact: '0998-590-7841' }
        ],
        areas: ['BARMM Region', 'Cotabato']
      }
    ]
  };

  const handleMarkerClick = (location) => {
    setSelectedLocation(location);
    setShowInfoPanel(true);
  };

  const closeInfoPanel = () => {
    setShowInfoPanel(false);
    setSelectedLocation(null);
  };

  return (
    <div className="philippines-map-container">
      <div className="map-legend">
        <div className="legend-item">
          <span className="legend-pin red"></span>
          <span>Head Office</span>
        </div>
        <div className="legend-item">
          <span className="legend-pin green"></span>
          <span>Branch Office</span>
        </div>
      </div>

      <div className="map-wrapper">
        <MapContainer
          center={philippinesCenter}
          zoom={6}
          style={{ height: '600px', width: '100%' }}
          zoomControl={false}
          scrollWheelZoom={true}
        >
          <ZoomControl position="bottomright" />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {/* Head Office Marker */}
          <Marker
            position={locations.headOffice.coords}
            icon={redIcon}
            eventHandlers={{
              click: () => handleMarkerClick(locations.headOffice)
            }}
          >
            <Popup>
              <strong>{locations.headOffice.name}</strong>
              <br />
              <small>Click for details</small>
            </Popup>
          </Marker>

          {/* Branch Markers */}
          {locations.branches.map((branch, index) => (
            <Marker
              key={`branch-${index}`}
              position={branch.coords}
              icon={greenIcon}
              eventHandlers={{
                click: () => handleMarkerClick({ ...branch, type: 'branch' })
              }}
            >
              <Popup>
                <strong>{branch.name}</strong>
                <br />
                <small>Click for details</small>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {/* Info Panel */}
      {showInfoPanel && selectedLocation && (
        <div className="location-info-panel">
          <button className="close-panel" onClick={closeInfoPanel}>
            <FiX />
          </button>
          
          <div className={`panel-header ${selectedLocation.type}`}>
            <FiMapPin className="panel-icon" />
            <h3>{selectedLocation.name}</h3>
            <span className="location-type">
              {selectedLocation.type === 'headquarters' ? 'HEAD OFFICE' : 'BRANCH OFFICE'}
            </span>
          </div>

          <div className="panel-body">
            {/* Address */}
            {selectedLocation.address && (
              <div className="info-row">
                <FiMapPin className="info-icon" />
                <div>
                  <label>Address</label>
                  <p>{selectedLocation.address}</p>
                </div>
              </div>
            )}

            {/* Manager */}
            {selectedLocation.manager && (
              <div className="info-row">
                <FiUser className="info-icon" />
                <div>
                  <label>Manager</label>
                  <p>{selectedLocation.manager.name}</p>
                  <small>{selectedLocation.manager.position}</small>
                </div>
              </div>
            )}

            {/* Contact Phone */}
            {selectedLocation.phone && (
              <div className="info-row">
                <FiPhone className="info-icon" />
                <div>
                  <label>Contact Number</label>
                  {Array.isArray(selectedLocation.phone) ? (
                    selectedLocation.phone.map((p, i) => <p key={i}>{p}</p>)
                  ) : (
                    <p>{selectedLocation.phone}</p>
                  )}
                </div>
              </div>
            )}

            {/* Office Staff */}
            {selectedLocation.staff && selectedLocation.staff.length > 0 && (
              <div className="info-row">
                <FiUsers className="info-icon" />
                <div>
                  <label>Office Staff</label>
                  <div className="staff-list">
                    {selectedLocation.staff.map((person, idx) => (
                      <div key={idx} className="staff-item">
                        <strong>{person.name}</strong>
                        <span>{person.position}</span>
                        {person.contact && <small>{person.contact}</small>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Coordinators */}
            {selectedLocation.coordinators && selectedLocation.coordinators.length > 0 && (
              <div className="info-row">
                <FiUsers className="info-icon" />
                <div>
                  <label>Coordinators & Supervisors</label>
                  <div className="coordinators-list">
                    {selectedLocation.coordinators.map((coord, idx) => (
                      <div key={idx} className="coordinator-item">
                        <strong>{coord.name}</strong>
                        <span>{coord.position}</span>
                        {coord.contact && <small>{coord.contact}</small>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Areas Covered */}
            {selectedLocation.areas && selectedLocation.areas.length > 0 && (
              <div className="info-row">
                <FiMapPin className="info-icon" />
                <div>
                  <label>Areas Covered</label>
                  <div className="areas-tags">
                    {selectedLocation.areas.map((area, idx) => (
                      <span key={idx} className="area-tag">{area}</span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PhilippinesMap;

